import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Nullable } from 'primeng/ts-helpers';
import { filter, map, startWith } from 'rxjs';
import { IconKey } from '../../../../../../../../whitelabel/icon-key.enum';
import { LanguageService } from '../../../../../../../core/services/language.service';
import { DropdownComponent } from '../../../../../../../osp-ui/components/form/dropdown/dropdown.component';
import {
  ErrorComponent,
} from '../../../../../../../osp-ui/components/form/form-field/components/error/error.component';
import {
  LabelComponent,
} from '../../../../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../../../../osp-ui/components/form/form-field/form-field.component';
import { InputTextComponent } from '../../../../../../../osp-ui/components/form/input-text/input-text.component';
import { OspTemplateDirective } from '../../../../../../../osp-ui/directives/osp-template.directive';
import { ActionOption } from '../../../../../../contract/action-option.contract';
import { FieldLimit } from '../../../../../../enums/field-limit.enum';
import { GeofenceNameExistValidator } from '../../../../../../validators/geofence-name-exist.validator';
import { ActionPanelComponent } from '../../../../../action-panel/action-panel.component';
import { ColorMarkerComponent } from '../../../../../color-marker/color-marker.component';
import { GenericCreateGeofenceCommand } from '../../../../contract/generic-create-geofence-command.contract';
import { GeofenceListItemOption } from '../../../../contract/geofence-list-item-option.contract';
import { GeofenceBuilderService } from '../../../../services/geofence-builder.service';

@Component({
  selector: 'osp-create-geofence-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ActionPanelComponent,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
    InputTextComponent,
    DropdownComponent,
    ColorMarkerComponent,
    OspTemplateDirective,
    NgTemplateOutlet,

  ],
  templateUrl: './create-geofence-form.component.html',
  styleUrl: './create-geofence-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateGeofenceFormComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly geofenceBuilderService = inject(GeofenceBuilderService);
  private readonly languageService = inject(LanguageService);

  public $map = input<google.maps.Map | Nullable>(null, { alias: 'map' });
  public $geofenceOptions = input<GeofenceListItemOption[]>([], { alias: 'geofenceOptions' });
  public $onReset = output({ alias: 'onReset' });
  public $onCreate = output<GenericCreateGeofenceCommand>({ alias: 'onCreate' });

  public readonly colors = [
    { label: this.translate('general.colors.red'), value: '#d50000' },
    { label: this.translate('general.colors.blue'), value: '#2962ff' },
    { label: this.translate('general.colors.green'), value: '#00c853' },
    { label: this.translate('general.colors.yellow'), value: '#ffff8d' },
    { label: this.translate('general.colors.orange'), value: '#f57c00' },
    { label: this.translate('general.colors.lightBlue'), value: '#0091ea' },
    { label: this.translate('general.colors.turquoise'), value: '#00bfa5' },
    { label: this.translate('general.colors.cyan'), value: '#00b8d4' },
    { label: this.translate('general.colors.purple'), value: '#aa00ff' },
    { label: this.translate('general.colors.darkPurple'), value: '#6a1b9a' },
    { label: this.translate('general.colors.lightGreen'), value: '#64dd17' },
    { label: this.translate('general.colors.yellowGreen'), value: '#aeea00' },
    { label: this.translate('general.colors.brown'), value: '#795548' },
    { label: this.translate('general.colors.gray'), value: '#757575' },
    { label: this.translate('general.colors.graublau'), value: '#607d8b' },
  ];
  nameMaxLength = FieldLimit.NAME;
  public form = new FormGroup({
    geofenceName: new FormControl<string>('', [Validators.required, Validators.maxLength(this.nameMaxLength)]),
    geofenceColor: new FormControl<string>(
      this.geofenceBuilderService.defaultColor,
    ),
  });
  public geofenceNameExistValidator: ValidatorFn | Nullable;

  private $canCompletePath = toSignal(this.geofenceBuilderService.canClosePath$);
  private $isPathClosed = toSignal(this.geofenceBuilderService.isPathClosed$);
  private $isInvalidShape = toSignal(this.geofenceBuilderService.isInvalidShape$);
  private $isFormValid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map((status) => status === 'VALID'),
    ),
  );
  private $isSaveButtonDisabled = computed(
    () => !this.$isFormValid() || !this.$isPathClosed() || this.$isInvalidShape(),
  );
  private $coords = toSignal(this.geofenceBuilderService.coords$);
  public $actions = signal<ActionOption[]>([]);
  public $message = computed<string>(() =>
    !this.$isInvalidShape()
      ? this.translate('shared.geofence.clickToCreate')
      : this.translate('shared.geofence.invalidShape'),
  );

  constructor() {
    effect(() => this.updateValidators(), { allowSignalWrites: true });
    effect(() => this.buildActions(), { allowSignalWrites: true });
  }

  public ngOnInit(): void {
    this.subscribeToColorChange();
    this.setGeofenceBuilderStroke();
    this.geofenceBuilderService.start(this.$map()!);
  }

  public ngOnDestroy(): void {
    this.geofenceBuilderService.reset();
  }

  private updateValidators(): void {
    const names = this.$geofenceOptions()
      .map(({ geofence }) => geofence.name);
    if (
      this.geofenceNameExistValidator &&
      this.form.controls.geofenceName.hasValidator(this.geofenceNameExistValidator)
    ) {
      this.form.controls.geofenceName.removeValidators(this.geofenceNameExistValidator);
    }
    this.geofenceNameExistValidator = GeofenceNameExistValidator.isValid(names);
    this.form.controls.geofenceName.addValidators(this.geofenceNameExistValidator);
    this.form.updateValueAndValidity();
  }

  private buildActions(): void {
    const closePathAction: ActionOption = {
      position: 'left',
      label: this.translate('shared.geofence.completePath'),
      action: () => this.geofenceBuilderService.completePath(),
      category: 'secondary',
    };
    const cancelAction: ActionOption = {
      position: 'right',
      label: this.translate('general.buttons.reset'),
      action: () => {
        this.geofenceBuilderService.reset();
        this.geofenceBuilderService.start(this.$map()!);
      },
      category: 'secondary',
    };
    const saveAction: ActionOption = {
      position: 'right',
      action: () => this.save(),
      label: this.translate('general.buttons.add'),
      $disabled: this.$isSaveButtonDisabled,
      tooltip: this.$isSaveButtonDisabled() ? this.translate('shared.geofence.completePathWarning') : undefined,
      tooltipPosition: 'bottom',
    };

    this.$actions.set([...(this.$canCompletePath() ? [closePathAction] : []), cancelAction, saveAction]);
  }

  private subscribeToColorChange(): void {
    this.form.controls.geofenceColor.valueChanges
      .pipe(startWith(this.form.controls.geofenceColor.value), filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe((newColor) => (this.geofenceBuilderService.color = newColor));
  }

  private setGeofenceBuilderStroke(): void {
    this.geofenceBuilderService.strokeColor = this.form.controls.geofenceColor.value ?? '';
    this.geofenceBuilderService.strokeWeight = 2;
  }

  private save(): void {
    const coords = this.$coords() ?? [];
    const geofenceData: GenericCreateGeofenceCommand = {
      geofenceName: this.form.controls.geofenceName.value!,
      geofenceColor: this.geofenceBuilderService.color,
      coordinates: [...coords, coords[0]].map((coord) => ({ lat: coord.lat(), lon: coord.lng() })),
    };

    this.$onCreate.emit(geofenceData);
  }

  private translate(key: string): string {
    return this.languageService.getInstant(key);
  }

  protected readonly IconKey = IconKey;
}
